import { type SQL, sql } from 'drizzle-orm';
import { inventoriables, inventory } from '../powersync/schemas/inventory.schema';
import { tasks } from '../powersync/schemas/tasks.schema';

/** The three buckets every list of tasks in either app splits them into. */
export const TASK_SCOPES = ['upcoming', 'overdue', 'completed'] as const;

export type TaskScope = (typeof TASK_SCOPES)[number];

/** The task columns that say whether it is closed. */
export type TaskClosingColumns = {
  status?: string | null;
  completed_at?: string | null;
};

/**
 * A task nobody has to do any more.
 *
 * A status says so outright. `completed_at` speaks only for a task with no status, which is how an
 * older "mark complete" path left it: a recurring task the legacy `tasks:restart` reopened keeps its
 * `completed_at` beside a `not_started` status, and is open. A recurring task this app completes
 * records the completion in `last_completed_at` instead, and stays open too.
 */
export const isTaskClosed = ({ status, completed_at }: TaskClosingColumns): boolean =>
  status === 'done' || status === 'cancelled' || (!status && Boolean(completed_at));

/**
 * `isTaskClosed` as a row predicate; the two must always agree. Both branches coalesce the status: a
 * bare `NULL IN (...)` is NULL, which would make this NULL for a task with no status and no
 * `completed_at`, and `NOT NULL` is NULL too, so that task would land in no bucket at all.
 */
const settled = (): SQL =>
  sql`(COALESCE(${tasks.status}, '') IN ('done', 'cancelled') OR (COALESCE(${tasks.status}, '') = '' AND ${tasks.completed_at} IS NOT NULL))`;

/**
 * Whether the task's due date has gone by.
 *
 * `datetime()` normalises both sides rather than comparing the stored strings directly: `due_date`
 * holds a mix of shapes — this app writes UTC `YYYY-MM-DD HH:mm:ss`, rows replicated from the
 * backend keep the API's ISO-8601 — and as plain text those sort against each other wrongly, since
 * `T` sorts after a space. SQLite evaluates `datetime('now')` on each run, so a task crosses into
 * Overdue on its own as time passes rather than when the query was built.
 */
const duePassed = (): SQL => sql`(${tasks.due_date} IS NOT NULL AND datetime(${tasks.due_date}) < datetime('now'))`;

/** Whether the task recurs by usage — `readTaskRecurrence`'s usage rule. */
const hasUsageRule = (): SQL => sql`COALESCE(${tasks.usage_interval}, 0) > 0`;

/** Whether the task recurs by date — `readTaskRecurrence`'s date rule. */
const hasDateRule = (): SQL =>
  sql`(COALESCE(${tasks.is_recurring}, 0) <> 0 AND COALESCE(${tasks.usage_time_unit}, 0) > 0)`;

/**
 * Whether the maintained item's meter has run `usage_interval` past the task's usage zero. The item
 * is the one the task is linked to (an `inventoriables` task link), as `completeTaskWithTx` reads it; an
 * item with no reading yet has not reached anything.
 */
const usageReached = (): SQL => sql`EXISTS (
  SELECT 1 FROM ${inventoriables}
  INNER JOIN ${inventory} ON ${inventory.id} = ${inventoriables.inventory_uuid}
  WHERE ${inventoriables.inventoriable_uuid} = ${tasks.id}
    AND ${inventoriables.inventoriable_type} = 'task'
    AND ${inventory.current_usage_value} IS NOT NULL
    AND ${inventory.current_usage_value} - COALESCE(${tasks.usage_baseline}, 0) >= ${tasks.usage_interval}
)`;

/**
 * Whether the task is due now — `isTaskDue` as a row predicate. A task with no usage rule is due once
 * its date has gone by. With one, a usage-only task is due once the meter reaches it; with both, `or`
 * is due when either is reached and `and` only once both are.
 */
const dueNow = (): SQL => sql`(CASE
  WHEN NOT ${hasUsageRule()} THEN ${duePassed()}
  WHEN NOT ${hasDateRule()} THEN ${usageReached()}
  WHEN ${tasks.recurrence_type} = 'and' THEN ${duePassed()} AND ${usageReached()}
  ELSE ${duePassed()} OR ${usageReached()}
END)`;

/** A task someone still has to do — Upcoming and Overdue together. */
export const openTasksWhere = (): SQL => sql`(NOT ${settled()})`;

/**
 * The row predicate for one bucket, shared by everything that counts or lists tasks in either app —
 * the dashboard widget's tabs, the counts above them, and a boat's own page. They must always agree
 * on which bucket a task falls in, which is the whole reason this lives in the package rather than
 * in whichever screen first needed it.
 *
 * A task with no due date counts as Upcoming rather than as Overdue: most tasks are entered
 * without one, and a bucket that swallowed all of them would make the distinction meaningless.
 */
export const tasksWhere = (scope: TaskScope): SQL => {
  if (scope === 'completed') {
    return settled();
  }

  if (scope === 'overdue') {
    return sql`(NOT ${settled()} AND ${dueNow()})`;
  }

  return sql`(NOT ${settled()} AND NOT ${dueNow()})`;
};
